import * as React from "react";
import { Checkbox } from "@blueprintjs/core";
import {
  ActionCode,
  AgentFunctionGridDto,
  EFunctionRequirement,
} from "../../../api";
import { FGCheckboxInput, useFGContext, useGridState } from "nsitools-react";
import { useCommonHooks } from "../../../hooks";

import styled, { css } from "styled-components";
import DirectionSelect from "../../../components/selects/DirectionSelect";
import {
  FormDataTable,
  PositionSelect,
  IDataTableColumn,
} from "../../../components";
import { Tooltip2 } from "@blueprintjs/popover2";

export interface AgentRequiredFunctionGridProps {
  loading: boolean;
  requirement: EFunctionRequirement;
  readonly: boolean;
}

const StyledHeader = styled.div`
  text-wrap: wrap;
  white-space: normal;
  max-width: 580px;
`;

const StyledDiv = styled.div`
  font-size: 11px;
  text-wrap: wrap;
  white-space: normal;
  max-width: 550px;
  font-style: italic;
`;

const StyledCheckBoxContainer = styled.div`
  display: flex;
  margin-left: 2rem;
`;

const StyledFormDataTable = styled(FormDataTable)`
  thead {
    background-color: #ddd;
  }
`;

export const AgentRequiredFunctionGrid: React.FunctionComponent<
  AgentRequiredFunctionGridProps
> = ({ loading, requirement, readonly }) => {
  const { t, hasAction, isSuperAdmin } = useCommonHooks();

  const tableState = useGridState<any>({
    serverMode: false,
    enablePagination: false,
    enableFilter: false,
    sortKeys: { order: "ASC" },
  });

  const { formik } = useFGContext();

  const { setData } = tableState;

  React.useEffect(() => {
    if (formik?.values?.agentFunctions) {
      setData(
        formik?.values?.agentFunctions.filter(
          (d) => d.requirement === requirement
        )
      );
    }
  }, [formik?.values?.agentFunctions, requirement, setData]);

  const isEditable = React.useMemo(
    () =>
      (hasAction([ActionCode.EditAgentFunction]) && !readonly) || isSuperAdmin,
    [hasAction, isSuperAdmin, readonly]
  );

  const getFunctionIndexById = React.useCallback(
    (id: number) => {
      return formik.values?.agentFunctions.findIndex((r) => r.id === id);
    },
    [formik.values?.agentFunctions]
  );

  const getSpecialityIndexById = React.useCallback(
    (functionId: number, id: number) => {
      return formik.values?.agentFunctions[
        getFunctionIndexById(functionId)
      ].specialities.findIndex((r) => r.id === id);
    },
    [formik.values?.agentFunctions, getFunctionIndexById]
  );

  const columns = React.useMemo<IDataTableColumn<AgentFunctionGridDto>[]>(
    () => [
      {
        fieldName: "function",
        width: "50%",
        disableSorting: true,
        header: () => (
          <StyledHeader>
            {requirement === EFunctionRequirement.Required
              ? t("RequiredFunctionByZoneAnalysis")
              : t("OptionalFunctionByZoneAnalysis")}
          </StyledHeader>
        ),
        render: (item: AgentFunctionGridDto) => (
          <>
            <b style={{ textWrap: "wrap" }}>{item.function}</b>
            <br />
            {item.specialities.map((f, index) => (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <StyledCheckBoxContainer>
                  <div
                    style={{
                      paddingRight: "0.5rem",
                      fontSize: "10px",
                    }}
                    key={index}
                  >
                    <FGCheckboxInput
                      noLabel
                      name={`agentFunctions[${getFunctionIndexById(
                        item.id
                      )}].specialities[${getSpecialityIndexById(
                        item.id,
                        f.id
                      )}].isChecked`}
                      checkBoxLabel={f.name}
                      renderReadOnlyValue={(v) => (
                        <Checkbox checked={v} label={f.name} />
                      )}
                    />
                  </div>
                </StyledCheckBoxContainer>
                <div>
                  {f.hasDirection ? (
                    <DirectionSelect
                      name={`agentFunctions[${getFunctionIndexById(
                        item.id
                      )}].specialities[${getSpecialityIndexById(
                        item.id,
                        f.id
                      )}].functionalDirectorId`}
                      readonly={!isEditable}
                      inline={true}
                      noLabel
                      style={{ minWidth: "250px" }}
                    />
                  ) : f.hasPosition ? (
                    <PositionSelect
                      readonly={!isEditable}
                      name={`agentFunctions[${getFunctionIndexById(
                        item.id
                      )}].specialities[${getSpecialityIndexById(
                        item.id,
                        f.id
                      )}].positionId`}
                      inline={true}
                      noLabel
                      style={{ minWidth: "250px" }}
                    />
                  ) : null}
                </div>
              </div>
            ))}
            <StyledDiv>
              <br />
              {item.note}
            </StyledDiv>
          </>
        ),
      },
      {
        fieldName: "isPerformed",
        header: () => (
          <Tooltip2 content={t("FunctionIsPerformed")}>
            {t("FunctionIsPerformedShort")}
          </Tooltip2>
        ),
        width: "5%",
        disableSorting: true,
        customizeCellStyle: () =>
          css`
            height: 100%;
            align-content: baseline;
          `,
        render: (item: AgentFunctionGridDto) => (
          <FGCheckboxInput
            noLabel
            name={`agentFunctions[${getFunctionIndexById(
              item.id
            )}].isPerformed`}
            renderReadOnlyValue={(v) => (
              <Checkbox checked={v} readOnly={isEditable} />
            )}
          />
        ),
      },
      {
        fieldName: "isHasTechAbilities",
        header: () => (
          <Tooltip2 content={t("FunctionHasTechAbilities")}>
            {t("FunctionHasTechAbilitiesShort")}
          </Tooltip2>
        ),
        disableSorting: true,
        width: "5%",
        customizeCellStyle: () =>
          css`
            height: 100%;
            align-content: baseline;
          `,
        render: (item: AgentFunctionGridDto) => (
          <FGCheckboxInput
            noLabel
            name={`agentFunctions[${getFunctionIndexById(
              item.id
            )}].isHasTechAbilities`}
            renderReadOnlyValue={(v) => (
              <Checkbox checked={v} disabled={isEditable} />
            )}
          />
        ),
      },
      {
        fieldName: "functionalDirector",
        width: "40%",
        textEllipsisAfterWidth: "260px",
        disableSorting: true,
        customizeCellStyle: () =>
          css`
            height: 100%;
            align-content: baseline;
          `,
        header: () => t("FunctionalDirector"),
      },
    ],
    [getFunctionIndexById, getSpecialityIndexById, isEditable, requirement, t]
  );

  return (
    <StyledFormDataTable
      tableState={tableState}
      columns={columns}
      loading={loading}
      tableLayoutFixed={true}
    />
  );
};

export default AgentRequiredFunctionGrid;
